import React from 'react';

import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSeparator,
} from '@/components/ui/combobox';
import type {
  ReportsFilterOptionGroup,
  ReportsFilterSelection,
} from 'components/Reports/hooks/useReportsFilterOptions';

type ReportsFilterBarProps = {
  groupedOptions: ReportsFilterOptionGroup[];
  selectedFilter: ReportsFilterSelection | null;
  onSelectedFilterChange: (selectedFilter: ReportsFilterSelection) => void;
  disableFilter?: boolean;
};

type ComboboxGroupItems = { label: string; items: ReportsFilterSelection[] };

const isSameOption = (
  a: ReportsFilterSelection | null,
  b: ReportsFilterSelection | null
) => a?.kind === b?.kind && a?.value === b?.value;

const ReportsFilterBar: React.FC<ReportsFilterBarProps> = ({
  groupedOptions,
  selectedFilter,
  onSelectedFilterChange,
  disableFilter = false,
}) => {
  const filterSelectId = 'reports-filter-select';
  const groups: ComboboxGroupItems[] = groupedOptions
    .filter((group) => group.options.length > 0)
    .map((group) => ({ label: group.label, items: group.options }));

  return (
    <div className="reports-filter-bar">
      <div className="reports-filter-bar__controls">
        <div className="reports-filter-bar__filter-control">
          <div className="reports-filter-bar__select-wrapper">
            <label className="visually-hidden" htmlFor={filterSelectId}>
              Area of responsibility or incident
            </label>
            <Combobox
              items={groups}
              value={selectedFilter}
              onValueChange={(next) => {
                if (next) onSelectedFilterChange(next);
              }}
              isItemEqualToValue={isSameOption}
              itemToStringLabel={(item) => item?.label ?? ''}
              autoHighlight
              disabled={disableFilter}
            >
              <ComboboxInput
                id={filterSelectId}
                className="tw:w-full"
                placeholder="Search areas or incidents"
                disabled={disableFilter}
              />
              <ComboboxContent className="dark">
                <ComboboxEmpty>No matching areas or incidents.</ComboboxEmpty>
                <ComboboxList>
                  {(group: ComboboxGroupItems, index: number) => (
                    <React.Fragment key={group.label}>
                      {index > 0 ? <ComboboxSeparator /> : null}
                      <ComboboxGroup items={group.items}>
                        <ComboboxLabel>{group.label}</ComboboxLabel>
                        <ComboboxCollection>
                          {(item: ReportsFilterSelection) => (
                            <ComboboxItem
                              key={`${item.kind}:${item.value}`}
                              value={item}
                            >
                              {item.label}
                            </ComboboxItem>
                          )}
                        </ComboboxCollection>
                      </ComboboxGroup>
                    </React.Fragment>
                  )}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsFilterBar;
